import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { initWebAnalytics, track, identify } from '../lib/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = unknown/loading, false = anon, object = logged in
    const [config, setConfig] = useState(null);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me');
            setUser(data.user);
            if (data.user?.id) identify(data.user.id, { email: data.user.email });
            return data.user;
        } catch (e) {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        initWebAnalytics();
        track('app.opened', { surface: 'pwa' });
        refreshMe();
        api.get('/config/public').then((r) => {
            setConfig(r.data);
            const theme = r.data?.theme;
            if (theme) {
                const root = document.documentElement;
                if (theme.primary) root.style.setProperty('--color-primary', theme.primary);
                if (theme.primary_dark) root.style.setProperty('--color-primary-dark', theme.primary_dark);
                if (theme.primary_light) root.style.setProperty('--color-primary-light', theme.primary_light);
                if (theme.brand_navy) root.style.setProperty('--color-brand-navy', theme.brand_navy);
            }
        }).catch(() => {});
    }, [refreshMe]);

    const login = async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        if (data?.access_token) localStorage.setItem('fs_token', data.access_token);
        setUser(data.user);
        identify(data.user.id, { email: data.user.email });
        track('user.logged_in', { surface: 'pwa' });
        return data.user;
    };

    const logout = async () => {
        try { await api.post('/auth/logout'); } catch {}
        localStorage.removeItem('fs_token');
        setUser(false);
        track('user.logged_out', { surface: 'pwa' });
    };

    return (
        <AuthContext.Provider value={{ user, setUser, config, refreshMe, login, logout, formatApiError }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
